import { useState, type ReactNode } from "react";
import type { OperationalQueues } from "../trips/selectors";
import type { Trip, TripRisk, TripStatus } from "../trips/types";
import { calculateFinancials } from "../trips/rules";
import { formatCurrency, formatPercent } from "../shared/currency";
import { StatusBadge } from "../shared/StatusBadge";
import { TablePager } from "../shared/TablePager";
import { usePagedRows } from "../shared/usePagedRows";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";

type DeskPageProps = {
  risks: TripRisk[];
  queues: OperationalQueues;
  activeTrips: Trip[];
  tripsById: Map<string, Trip>;
  onNewTrip: () => void;
  onStatusChange: (tripId: string, status: TripStatus) => void;
};

type DeskTab = "exceptions" | "active" | "delivered" | "overdue";

export function DeskPage({
  risks,
  queues,
  activeTrips,
  tripsById,
  onNewTrip,
  onStatusChange,
}: DeskPageProps) {
  const [activeTab, setActiveTab] = useState<DeskTab>("exceptions");
  const counts: Record<DeskTab, number> = {
    exceptions: risks.length,
    active: activeTrips.length,
    delivered: queues.deliveredNotBilled.length,
    overdue: queues.overdueReceivables.length,
  };
  const queueOptions: SelectOption<DeskTab>[] = deskTabs.map((tab) => ({
    value: tab.id,
    label: `${tab.label} (${counts[tab.id]})`,
  }));

  return (
    <section className="orbit-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-3 sm:px-4">
        <div>
          <h2 className="font-semibold text-slate-950">Desk</h2>
          <p className="hidden text-sm text-slate-500 sm:block">
            Operational queues and follow-up work.
          </p>
        </div>
        <CompactSelect
          ariaLabel="Desk queue"
          value={activeTab}
          options={queueOptions}
          onChange={setActiveTab}
          className="w-[170px] lg:hidden"
        />
        <div className="orbit-raised hidden grid-cols-4 gap-1 p-1 lg:grid">
          {deskTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex min-w-0 flex-col items-center gap-0.5 rounded-md px-1 py-2 text-[11px] font-semibold sm:flex-row sm:gap-2 sm:px-3 sm:text-sm ${
                activeTab === tab.id ? "orbit-tab-active" : "orbit-tab"
              }`}
            >
              {tab.label}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                {counts[tab.id]}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="px-3 py-2 sm:px-4 sm:py-3">
        {activeTab === "exceptions" ? (
          <RiskTable risks={risks} tripsById={tripsById} />
        ) : null}
        {activeTab === "active" ? (
          <ActiveTable
            trips={activeTrips}
            onNewTrip={onNewTrip}
            onStatusChange={onStatusChange}
          />
        ) : null}
        {activeTab === "delivered" ? (
          <TripQueueTable
            trips={queues.deliveredNotBilled}
            emptyText="No delivered trips are waiting for billing."
          />
        ) : null}
        {activeTab === "overdue" ? (
          <TripQueueTable
            trips={queues.overdueReceivables}
            emptyText="No overdue receivables right now."
          />
        ) : null}
      </div>
    </section>
  );
}

const deskTabs: Array<{ id: DeskTab; label: string }> = [
  { id: "exceptions", label: "Exceptions" },
  { id: "active", label: "Active" },
  { id: "delivered", label: "Not Billed" },
  { id: "overdue", label: "Overdue" },
];

function RiskTable({
  risks,
  tripsById,
}: {
  risks: TripRisk[];
  tripsById: Map<string, Trip>;
}) {
  const { page, pageCount, pageRows, nextPage, previousPage } = usePagedRows(risks);

  if (risks.length === 0) return <CompactEmpty text="No exceptions need attention right now." />;

  return (
    <>
      <div className="divide-y divide-slate-100 lg:hidden">
        {pageRows.map((risk) => {
          const trip = tripsById.get(risk.tripId);
          const route = [trip?.origin, trip?.destination].filter(Boolean).join(" - ");
          return (
            <article key={risk.id} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${riskTone[risk.severity]}`}>
                    {risk.label}
                  </span>
                  <p className="mt-2 truncate font-semibold text-slate-950">
                    {trip?.vehicleNo || trip?.tripNo || risk.tripId}
                  </p>
                  <p className="truncate text-xs text-slate-500">{route || "Route not set"}</p>
                </div>
                <p className="shrink-0 text-sm font-bold text-slate-950">
                  {formatCurrency(risk.moneyImpact)}
                </p>
              </div>
              <p className="mt-2 text-sm text-slate-600">{risk.detail}</p>
            </article>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[780px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Risk</th>
              <th className="px-3 py-2">Trip</th>
              <th className="px-3 py-2">Detail</th>
              <th className="px-3 py-2 text-right">Impact</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((risk) => {
              const trip = tripsById.get(risk.tripId);
              const route = [trip?.origin, trip?.destination].filter(Boolean).join(" - ");
              return (
                <tr key={risk.id}>
                  <td className="px-3 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${riskTone[risk.severity]}`}>
                      {risk.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-slate-950">
                      {trip?.vehicleNo || trip?.tripNo || risk.tripId}
                    </p>
                    <p className="text-xs text-slate-500">{route || "Route not set"}</p>
                  </td>
                  <td className="px-3 py-3 text-slate-600">{risk.detail}</td>
                  <td className="px-3 py-3 text-right font-bold text-slate-950">
                    {formatCurrency(risk.moneyImpact)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePager
        page={page}
        pageCount={pageCount}
        totalRows={risks.length}
        onPrevious={previousPage}
        onNext={nextPage}
      />
    </>
  );
}

function TripQueueTable({
  trips,
  emptyText,
}: {
  trips: Trip[];
  emptyText: string;
}) {
  const { page, pageCount, pageRows, nextPage, previousPage } = usePagedRows(trips);

  if (trips.length === 0) return <CompactEmpty text={emptyText} />;

  return (
    <>
      <div className="divide-y divide-slate-100 lg:hidden">
        {pageRows.map((trip) => {
          const financials = calculateFinancials(trip);
          return (
            <article key={trip.id} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-950">{trip.tripNo}</p>
                  <p className="truncate text-xs text-slate-500">
                    {trip.partyName || "Party not set"} · {trip.origin} - {trip.destination}
                  </p>
                </div>
                <StatusBadge status={trip.status} />
              </div>
              <p className="mt-2 text-right text-sm font-bold text-amber-700">
                Due {formatCurrency(financials.outstanding)}
              </p>
            </article>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Trip</th>
              <th className="px-3 py-2">Party</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Outstanding</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((trip) => {
              const financials = calculateFinancials(trip);
              return (
                <tr key={trip.id}>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-slate-950">{trip.tripNo}</p>
                    <p className="text-xs text-slate-500">
                      {trip.origin} - {trip.destination}
                    </p>
                  </td>
                  <td className="px-3 py-3">{trip.partyName || "Party not set"}</td>
                  <td className="px-3 py-3">
                    <StatusBadge status={trip.status} />
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-amber-700">
                    {formatCurrency(financials.outstanding)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePager
        page={page}
        pageCount={pageCount}
        totalRows={trips.length}
        onPrevious={previousPage}
        onNext={nextPage}
      />
    </>
  );
}

function ActiveTable({
  trips,
  onNewTrip,
  onStatusChange,
}: {
  trips: Trip[];
  onNewTrip: () => void;
  onStatusChange: (tripId: string, status: TripStatus) => void;
}) {
  const { page, pageCount, pageRows, nextPage, previousPage } = usePagedRows(trips);

  if (trips.length === 0) {
    return (
      <CompactEmpty
        text="No active trips. Create a planned trip or move one to dispatched."
        action={
          <button
            type="button"
            onClick={onNewTrip}
            className="orbit-button-primary mt-3 px-4 py-2 text-sm"
          >
            New Trip
          </button>
        }
      />
    );
  }

  return (
    <>
      <div className="divide-y divide-slate-100 lg:hidden">
        {pageRows.map((trip) => {
          const financials = calculateFinancials(trip);
          return (
            <article key={trip.id} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-950">{trip.vehicleNo}</p>
                  <p className="truncate text-xs text-slate-500">
                    {trip.partyName} · {trip.origin} - {trip.destination}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-600">
                    Margin {formatPercent(financials.marginPct)}
                  </p>
                </div>
                <StatusBadge status={trip.status} />
              </div>
              <button
                type="button"
                onClick={() => onStatusChange(trip.id, "Delivered")}
                className="mt-3 min-h-11 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
              >
                Mark Delivered
              </button>
            </article>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Trip</th>
              <th className="px-3 py-2">Party</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Margin</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((trip) => {
              const financials = calculateFinancials(trip);
              return (
                <tr key={trip.id}>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-slate-950">{trip.vehicleNo}</p>
                    <p className="text-xs text-slate-500">
                      {trip.origin} - {trip.destination}
                    </p>
                  </td>
                  <td className="px-3 py-3">{trip.partyName}</td>
                  <td className="px-3 py-3">
                    <StatusBadge status={trip.status} />
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-slate-700">
                    {formatPercent(financials.marginPct)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onStatusChange(trip.id, "Delivered")}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Mark Delivered
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePager
        page={page}
        pageCount={pageCount}
        totalRows={trips.length}
        onPrevious={previousPage}
        onNext={nextPage}
      />
    </>
  );
}

const riskTone = {
  critical: "orbit-tone-rose",
  warning: "orbit-tone-amber",
  info: "orbit-tone-blue",
};

function CompactEmpty({
  text,
  action,
}: {
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
      <p className="text-sm font-medium text-slate-500">{text}</p>
      {action}
    </div>
  );
}
